import { num } from '../format';

/**
 * The shell: toasts, the language and sound toggles, the confirmation the
 * whole app asks, and the three labels the shared vocabulary carries
 * (App.tsx, components/LangToggle.tsx, ui/index.tsx).
 *
 * See web/src/lib/strings/index.ts for how this file is shaped and why, and
 * docs/i18n.md for the Arabic glossary every value here must follow. Neither
 * object may be `as const`: `ar` is typed against `en`, and literal types
 * would make it have to repeat the English words.
 *
 * Numbers go through lib/format with an explicit locale rather than the
 * ambient one, because a catalogue is a value: `stringsFor('ar')` has to
 * build Arabic sentences whether or not the page being read is Arabic.
 */

export const en = {
  /**
   * The language switch (components/LangToggle.tsx).
   *
   * The name arrives as an endonym — `العربية` when the page is English,
   * `English` when it is Arabic — so the sentence is in the language the
   * reader is leaving and the name is in the one they are going to. That is
   * the point of the control: the person who needs it is the person who
   * cannot read the page it is sitting on.
   */
  switchTo: (language: string) => `Switch to ${language}`,
  /** The same control once it offers more than two languages and opens a
   *  list (LangToggle.tsx, `LangMenu`). */
  chooseLanguage: 'Choose a language',

  /**
   * Where the learner's commands run, as the level list says it (App.tsx,
   * `DRIVER_LABEL`). `Linux` is a name and stays Latin in both languages;
   * the rest of the phrase is ordinary prose and does not.
   */
  driverLabel: 'Linux in this tab',

  /* What the app says in passing, over the top of whatever is on screen. */
  toast: {
    /* The two sentences a learner gets on the way back from Stripe, and
       between them they are the only place this product is tempted to say
       something about money it has not checked.

       `checkoutCancelled` used to end "Nothing was charged." It fires from
       `?checkout=cancelled`, which is written by `bounceHome` off the
       `cancel_url` handed to Stripe (worker/stripe.ts) — a query parameter on
       a URL the learner is holding, on a route that is not secret, which that
       file's own comments call "not evidence" twice. Nothing had been asked
       of Stripe and nothing of the Worker that could answer it: the bounce is
       a redirect which reads that one parameter and writes it out again, and
       it grants nothing and checks nothing on purpose. So the tab was
       asserting a fact about somebody's bank account on the strength of a
       string in the address bar. The rare case it is wrong in is the
       expensive one: a card that went through while the redirect went astray,
       read by somebody who has just been told nothing was charged.

       So it reports what it actually has — the page came back without a
       payment — and hands over the one thing that does settle it. Reloading
       is not a nicety here: the Worker is asked what this account owns on
       every load (`usePaid`), and the signed webhook is what will have
       decided it — which is also why "reload" is the advice and not "try
       again": a webhook that has not landed yet lands without the learner
       doing anything. `checkoutThanks` had the shape right already and is
       untouched: it thanks, and then it says it is going to go and check. */
    checkoutCancelled: 'Checkout closed without a payment. If your bank says otherwise, reload the page.',
    checkoutThanks: 'Thank you — checking your payment…',
    /* The range is the paid half of the course, and it moves when the course
       grows, so it is an argument rather than two digits in a sentence.

       Two sentences for the same purchase, because there are two true things
       to say about it and which one is true is a fact about the catalogue.
       A range is legible only while the paid half is the tail of one lane:
       it names where the free half stops. A wholly paid specialization has
       no such boundary inside it, and its own numbering starts at zero like
       every other track's — so "levels 0-19 unlocked" would quote a run of
       numbers the learner already played under another name. The count is
       true whatever shape the paid half is in, which is why it is the one
       that carries when `paidSpan` refuses a range (lib/entitlement.ts, and
       the same pair in home.ts's invite dialog). */
    checkoutUnlocked: (from: number, to: number) =>
      `Levels ${num(from, 'en')}-${num(to, 'en')} unlocked. Have at it.`,
    checkoutUnlockedCount: (count: number) =>
      `${num(count, 'en')} level${count === 1 ? '' : 's'} unlocked. Have at it.`,
    signInFirst: 'Sign in first, so the course has an account to unlock.',
    paymentsOff: 'Payments are not switched on yet.',
    checkoutFailed: 'Could not start the payment. Try again in a moment.',
    /** Refused before anything reached the processor, so the useful half of
     *  this sentence is the second one: nothing happened, and nothing needs
     *  to. */
    /** The course is a period now, one month or three: owning it is "your
     *  period is running", and the renewal opens in its last fourteen days
     *  (worker/stripe.ts). The length is not said; either is possible. */
    checkoutOwned: 'Your access to the course is still running. Renewing opens in its last fourteen days. Nothing was charged.',
    /** Landing in the tab a pack was bought in (lib/checkout.ts,
     *  `startPackCheckout`): the level is in the other tab, untouched. */
    noSuchLevel: 'That level does not exist.',
    /** A benchmark task's link (docs/efficiency-bench.md), opened signed out. */
    benchSignIn: 'Sign in to open a benchmark task.',
  },

  /* The sound toggle: a `title` and an `aria-label` for each state. The title
     says what the state is and what a click does; the label says only what
     the click does, because a screen reader already reads `aria-pressed`. */
  sound: {
    titleMuted: 'Sound off. Click to enable.',
    titleOn: 'Sound on. Click to mute.',
    labelMuted: 'Enable sound',
    labelOn: 'Mute sound',
  },

  /* The shared vocabulary (ui/index.tsx). The wordmark itself is not here:
     `EngineerOS` is the product's name and it is set in Latin in both
     languages, in the markup, beside the comment that says so. */
  ui: {
    brandHome: 'EngineerOS home',
    /** The fallback name of a meter that was given none. */
    progress: 'progress',
    stars: (value: number) => `${num(value, 'en')} of 3 stars`,
  },

  /* The theme switch (components/ThemeToggle.tsx), shaped like the sound
     toggle above it: a `title` naming the state and what a click does, and an
     `aria-label` saying only what the click does.

     The titles are named for the state they are shown in, and the dark one
     says "the default" out loud. That word is the whole difference between a
     preference and a mistake: somebody who hit this by accident has to be
     able to tell which of the two they were supposed to be looking at. */
  theme: {
    titleDark: 'Dark theme — the default. Click for light.',
    titleLight: 'Light theme. Click to go back to dark.',
    labelDark: 'Use the light theme',
    labelLight: 'Use the dark theme',
  },

  /* The fold that tucks the whole corner cluster into the edge of the window
     (components/CornerFold.tsx), shaped like the two toggles above it: a
     `title` naming the state and what a click does, and an `aria-label`
     saying only what the click does.

     "Controls" and not "icons": what folds away is the sound, the language,
     the theme and the way into chat, and somebody hunting for any of the four
     is hunting for it by name. The third pair exists for the one state the
     other two cannot describe — folded, with something waiting in chat behind
     it — because the only thing that says so on screen is a dot, and a dot
     does not read aloud. */
  corner: {
    titleShown: 'Fold these controls into the edge, out of the way of the text.',
    titleFolded: 'Controls folded away. Click to bring them back.',
    titleFoldedNew: 'Controls folded away, and something new is in chat. Click to bring them back.',
    labelShown: 'Fold the corner controls away',
    labelFolded: 'Bring the corner controls back',
    labelFoldedNew: 'Bring the corner controls back — something new in chat',
  },
};

/**
 * Arabic counts its nouns, which is why a sentence with a number in it is a
 * function here as it is in home.ts and reading.ts.
 *
 * 3 to 10 take the plural (`5 مستويات`); 11 and up take the singular in the
 * accusative (`32 مستوى`). One and two have forms of their own, and the paid
 * half of a course is neither — the singular covers them, as it does in the
 * two files above, rather than adding a form for a catalogue nobody would
 * ship.
 */
const plural = (n: number, few: string, one: string) => (n >= 3 && n <= 10 ? few : one);

export const ar: typeof en = {
  switchTo: (language: string) => `استخدم ${language}`,
  chooseLanguage: 'اختر اللغة',

  driverLabel: 'Linux يعمل في هذا التبويب',

  toast: {
    /* `أُغلقت صفحة الدفع` and not `أُلغيت عملية الدفع`: the glossary's
       `صفحة الدفع` is the checkout page, and what is known is that the page
       closed, not that the payment was cancelled. The old Arabic carried the
       same untrue second half as the English (`لم يُخصم شيء` — nothing was
       charged), and it drops for the same reason. */
    checkoutCancelled: 'أُغلقت صفحة الدفع دون إتمام الدفع. إن أفادك مصرفك بغير ذلك، فأعد تحميل الصفحة.',
    checkoutThanks: 'شكرًا — نتحقق من عملية الدفع…',
    checkoutUnlocked: (from: number, to: number) =>
      `أصبحت المستويات ${num(from, 'ar')}-${num(to, 'ar')} متاحة. ابدأ وقتما تشاء.`,
    /* `أصبح لديك` puts the verb in front of the count, which is where Arabic
       lets it stay singular whatever follows — the same move the invite
       dialog's `redeemedCount` makes, and for the same reason: agreeing
       backwards with `مستويات` and then with `مستوى` would need a third form
       for no gain in meaning. The adjective travels inside `plural` rather
       than being appended after it, because `مستويات` is a broken plural and
       takes a feminine singular (`متاحة`) while the counted singular takes
       the accusative (`مستوى متاحًا`), and one adjective stuck on the end
       would agree with one of them and not the other.

       No isolate around the digits: a number with Arabic on both sides of it
       is already read as one left-to-right run, and the range version above
       has carried two of them unisolated since it shipped. */
    checkoutUnlockedCount: (count: number) =>
      `أصبح لديك ${num(count, 'ar')} ${plural(count, 'مستويات متاحة', 'مستوى متاحًا')}. ابدأ وقتما تشاء.`,
    signInFirst: 'سجّل الدخول أولًا لنضيف الدورة إلى حسابك.',
    paymentsOff: 'الدفع غير متاح حاليًا.',
    checkoutFailed: 'تعذّر بدء عملية الدفع. أعد المحاولة بعد قليل.',
    checkoutOwned: 'وصولك إلى الدورة ما زال ساريًا. يُفتح التجديد في آخر أربعة عشر يومًا منه. لم يُخصم شيء.',
    noSuchLevel: 'هذا المستوى غير موجود.',
    benchSignIn: 'سجّل الدخول لتفتح مهمة من مهام الاختبار.',
  },

  sound: {
    titleMuted: 'الصوت متوقف. انقر لتشغيله.',
    titleOn: 'الصوت يعمل. انقر لإيقافه.',
    labelMuted: 'تشغيل الصوت',
    labelOn: 'كتم الصوت',
  },

  ui: {
    brandHome: 'الصفحة الرئيسية لـ EngineerOS',
    progress: 'التقدم',
    stars: (value: number) => `${num(value, 'ar')} من 3 نجوم`,
  },

  theme: {
    titleDark: 'المظهر الداكن هو الافتراضي. انقر لاستخدام المظهر الفاتح.',
    titleLight: 'المظهر الفاتح. انقر لاستخدام المظهر الداكن.',
    labelDark: 'استخدام المظهر الفاتح',
    labelLight: 'استخدام المظهر الداكن',
  },

  corner: {
    titleShown: 'اطوِ هذه الأدوات إلى حافة النافذة لتبتعد عن النص.',
    titleFolded: 'الأدوات مطوية. انقر لإعادتها.',
    titleFoldedNew: 'الأدوات مطوية، وهناك جديد في المحادثة. انقر لإعادتها.',
    labelShown: 'طيّ أدوات الزاوية',
    labelFolded: 'إعادة أدوات الزاوية',
    labelFoldedNew: 'إعادة أدوات الزاوية — هناك جديد في المحادثة',
  },
};

/**
 * German. The reader is `du`, as everywhere in the German half (docs/i18n.md
 * §9), and a count agrees the way English does — one form for one, one for
 * the rest — so the sentences stay single expressions.
 */
export const de: typeof en = {
  switchTo: (language: string) => `Zu ${language} wechseln`,
  chooseLanguage: 'Sprache wählen',

  driverLabel: 'Linux in diesem Tab',

  toast: {
    checkoutCancelled: 'Der Bezahlvorgang wurde ohne Zahlung geschlossen. Falls deine Bank etwas anderes meldet, lade die Seite neu.',
    checkoutThanks: 'Danke – wir prüfen deine Zahlung …',
    checkoutUnlocked: (from: number, to: number) =>
      `Level ${num(from, 'de')}–${num(to, 'de')} freigeschaltet. Leg los.`,
    checkoutUnlockedCount: (count: number) =>
      `${num(count, 'de')} Level freigeschaltet. Leg los.`,
    signInFirst: 'Melde dich zuerst an, damit der Kurs einem Konto freigeschaltet werden kann.',
    paymentsOff: 'Zahlungen sind noch nicht eingeschaltet.',
    checkoutFailed: 'Die Zahlung konnte nicht gestartet werden. Versuch es gleich noch einmal.',
    checkoutOwned: 'Dein Zugang zum Kurs läuft noch. Verlängern kannst du in seinen letzten vierzehn Tagen. Es wurde nichts berechnet.',
    noSuchLevel: 'Dieses Level gibt es nicht.',
    benchSignIn: 'Melde dich an, um eine Benchmark-Aufgabe zu öffnen.',
  },

  sound: {
    titleMuted: 'Ton aus. Klicken zum Einschalten.',
    titleOn: 'Ton an. Klicken zum Stummschalten.',
    labelMuted: 'Ton einschalten',
    labelOn: 'Ton stummschalten',
  },

  ui: {
    brandHome: 'EngineerOS-Startseite',
    progress: 'Fortschritt',
    stars: (value: number) => `${num(value, 'de')} von 3 Sternen`,
  },

  theme: {
    titleDark: 'Dunkles Design – die Voreinstellung. Klicken für hell.',
    titleLight: 'Helles Design. Klicken, um zu Dunkel zurückzukehren.',
    labelDark: 'Helles Design verwenden',
    labelLight: 'Dunkles Design verwenden',
  },

  corner: {
    titleShown: 'Diese Bedienelemente an den Rand klappen, weg vom Text.',
    titleFolded: 'Bedienelemente eingeklappt. Klicken, um sie zurückzuholen.',
    titleFoldedNew: 'Bedienelemente eingeklappt, und im Chat gibt es etwas Neues. Klicken, um sie zurückzuholen.',
    labelShown: 'Bedienelemente in der Ecke einklappen',
    labelFolded: 'Bedienelemente in der Ecke zurückholen',
    labelFoldedNew: 'Bedienelemente in der Ecke zurückholen – Neues im Chat',
  },
};

export const ja: typeof en = {
  switchTo: (language: string) => `${language}に切り替える`,
  chooseLanguage: '言語を選択',

  driverLabel: 'このタブで動くLinux',

  toast: {
    checkoutCancelled: '支払いが行われないまま購入手続きが閉じられました。銀行から別の通知が届いている場合は、ページを再読み込みしてください。',
    checkoutThanks: 'ありがとうございます。お支払いを確認しています…',
    checkoutUnlocked: (from: number, to: number) =>
      `レベル${num(from, 'ja')}〜${num(to, 'ja')}がアンロックされました。さっそく始めましょう。`,
    checkoutUnlockedCount: (count: number) =>
      `${num(count, 'ja')}レベルがアンロックされました。さっそく始めましょう。`,
    signInFirst: '先にログインしてください。コースをアンロックするアカウントが必要です。',
    paymentsOff: 'お支払いはまだ有効になっていません。',
    checkoutFailed: 'お支払いを開始できませんでした。少し待ってからもう一度お試しください。',
    checkoutOwned: 'コースの利用期間はまだ続いています。更新は期間の最後の14日間に開きます。請求は発生していません。',
    noSuchLevel: 'そのレベルは存在しません。',
    benchSignIn: 'ベンチマークの課題を開くにはログインしてください。',
  },

  sound: {
    titleMuted: 'サウンドオフ。クリックでオンにします。',
    titleOn: 'サウンドオン。クリックでミュートします。',
    labelMuted: 'サウンドをオンにする',
    labelOn: 'サウンドをミュートする',
  },

  ui: {
    brandHome: 'EngineerOSのホーム',
    progress: '進捗',
    stars: (value: number) => `3つ星のうち${num(value, 'ja')}`,
  },

  theme: {
    titleDark: 'ダークテーマ(デフォルト)。クリックでライトに切り替えます。',
    titleLight: 'ライトテーマ。クリックでダークに戻します。',
    labelDark: 'ライトテーマを使う',
    labelLight: 'ダークテーマを使う',
  },

  corner: {
    titleShown: 'このコントロールを画面の端にたたんで、テキストの邪魔にならないようにします。',
    titleFolded: 'コントロールをたたんでいます。クリックで元に戻します。',
    titleFoldedNew: 'コントロールをたたんでいます。チャットに新着があります。クリックで元に戻します。',
    labelShown: '隅のコントロールをたたむ',
    labelFolded: '隅のコントロールを戻す',
    labelFoldedNew: '隅のコントロールを戻す(チャットに新着あり)',
  },
};
